import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useStore } from 'react-redux';
import { OnboardingV2State, OnboardingWorkCoverageKind } from '../../store/onboardingV2/types.ts';
import { onboardingAgent, AgentQuestion, AgentMessage } from '../../services/onboardingAgent/index.ts';
import { OnboardingStep } from '../../utils/onboardingV2/conversationalOnboardingSteps.ts';
import { setLoginAnimation, setLoginAnimationType } from '../../store/actions/authActions.js';
import { openMainModal } from '../../store/actions/profileActions';
import {
  COMMERCE_DELIVERY_RADIUS_KM_MIN,
  COMMERCE_DELIVERY_RADIUS_UI_KM_MAX,
  COMMERCE_LISTED_ZONES_ID_MAX,
  COMMERCE_LISTED_ZONES_ID_MIN,
  createCommerce,
  type CommerceDeliveryScope,
} from '../../services/commerceService.ts';
import { userHasCommerceOnboardingCategory } from '../../utils/commerceOnboardingEligibility.ts';
import { userHasCommerceRegistration } from '../../utils/userCommerces.ts';
import { ONBOARDING_WORK_ZONES_AGENT } from '../../utils/onboardingV2/onboardingConstants.ts';
import { hasWorkZonesStepCompleted } from '../../utils/onboardingV2/workZonesStep.ts';
import {
  mergeOnboardingJobBoardCoverage,
  onboardingJobBoardSnapshotAllCountry,
  onboardingJobBoardSnapshotSpecificZones,
} from '../../utils/workCoverage/jobBoardCoverageModel.ts';

export interface Message {
  id: string;
  role: 'assistant' | 'user';
  content: string;
  timestamp: string;
  question?: Question | null;
}

export type Question = AgentQuestion;

interface UseConversationalOnboardingProps {
  state: OnboardingV2State;
  setAccountType: (type: 'PERSON' | 'COMPANY') => void;
  setPersonaForm: (formData: any) => void;
  setEmpresaForm: (formData: any) => void;
  setUserSkills: (skills: any[]) => void;
  addSkill: (skill: any) => void;
  removeSkill: (skillId: number) => void;
  setWorkZones: (zones: any[]) => void;
  setAllCountry: (allCountry: boolean) => void;
  setWorkCoverageKind: (kind: OnboardingWorkCoverageKind) => void;
  setInfluenceMap: (payload: {
    lat?: number | null;
    lng?: number | null;
    radiusKm?: number;
    placeLabel?: string | null;
  }) => void;
  setUserImage: (image: string | null) => void;
  setSelectedCategoryFilter: (categoryId: number | null) => void;
  setPreferredProfileCategories: (categoryIds: number[]) => void;
  removePreferredProfileCategory: (categoryId: number) => void;
  updateProfile: () => Promise<any>;
  loadSkillsAndCategories: () => Promise<any>;
  getCities: (provinceId: number) => Promise<any>;
}

export const useConversationalOnboarding = ({
  state,
  setAccountType,
  setPersonaForm,
  setEmpresaForm,
  setUserSkills,
  addSkill,
  removeSkill,
  setWorkZones,
  setAllCountry,
  setWorkCoverageKind,
  setInfluenceMap,
  setUserImage,
  setSelectedCategoryFilter,
  setPreferredProfileCategories,
  removePreferredProfileCategory,
  updateProfile,
  loadSkillsAndCategories,
  getCities,
}: UseConversationalOnboardingProps) => {
  const dispatch = useDispatch();
  const store = useStore();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [completedMessageId, setCompletedMessageId] = useState<string | null>(null);
  const [showReturnToFinish, setShowReturnToFinish] = useState(false);
  const navigate = useNavigate();
  const initializedRef = useRef<boolean>(false);
  const pendingSkillResetRef = useRef<boolean>(false);
  const reviewModeRef = useRef<boolean>(false);
  const reviewFieldsRef = useRef<string[]>([]);
  const reviewIndexRef = useRef<number>(0);
  const pendingQuestionRef = useRef<Question | null>(null);
  const activeTypingMessageIdRef = useRef<string | null>(null);
  const typingTimeoutRef = useRef<number | null>(null);
  const workZonesInteractiveSettledRef = useRef(false);

  const hasProgress = useCallback(() => {
    return !!(
      state.accountType ||
      (state.accountType === 'PERSON' && (state.personaForm.name || state.personaForm.lastName || state.personaForm.tel)) ||
      (state.accountType === 'COMPANY' && (state.empresaForm.companyName || state.empresaForm.tel)) ||
      state.userSkills.length > 0 ||
      hasWorkZonesStepCompleted(state)
    );
  }, [state]);

  const validateRequiredOnboardingData = useCallback((currentState: OnboardingV2State) => {
    const hasSkills = currentState.userSkills.length > 0;
    const hasProfiles = (currentState.preferredProfileCategories?.length ?? 0) > 0;
    const hasZones = hasWorkZonesStepCompleted(currentState);
    const valid = hasSkills && hasProfiles && hasZones;
    const missing: ('skills' | 'profiles' | 'zones')[] = [];
    if (!hasSkills) missing.push('skills');
    if (!hasProfiles) missing.push('profiles');
    if (!hasZones) missing.push('zones');
    return { valid, missing };
  }, []);

  const getValidationErrorMessage = useCallback((missing: ('skills' | 'profiles' | 'zones')[]) => {
    if (missing.length === 0) return '';
    const labels: Record<'skills' | 'profiles' | 'zones', string> = {
      skills: 'habilidades',
      profiles: 'categorías de perfiles con quien conectar',
      zones: ONBOARDING_WORK_ZONES_AGENT.missingZonesListLabel,
    };
    const list = missing.map((m) => labels[m]).join(', ');
    return missing.length === 1
      ? (missing[0] === 'skills' && 'Agregá al menos una habilidad para continuar.')
      || (missing[0] === 'profiles' && 'Seleccioná al menos una categoría de perfiles con quien conectar.')
      || (missing[0] === 'zones' && ONBOARDING_WORK_ZONES_AGENT.incompleteContinue)
      : `Para continuar completá: ${list}. Podés revisar el panel de la derecha.`;
  }, []);

  const getQuestionForMissingStep = useCallback((missing: 'skills' | 'profiles' | 'zones'): Question => {
    const map: Record<'skills' | 'profiles' | 'zones', Question> = {
      skills: { id: 'skills', type: 'interactive', interactiveType: 'skills' },
      profiles: { id: 'profiles', type: 'interactive', interactiveType: 'profiles' },
      zones: { id: 'workZones', type: 'interactive', interactiveType: 'workZones' },
    };
    return map[missing];
  }, []);
  
  const normalizeQuestion = useCallback((question?: Question | null): Question | null => {
    if (!question) return null;
    const normalizedField = question.field === 'description' ? 'bio' : question.field;
    const normalizedId = question.id === 'description' ? 'bio' : question.id;
    if (normalizedField === question.field && normalizedId === question.id) {
      return question;
    }
    return {
      ...question,
      field: normalizedField,
      id: normalizedId,
    };
  }, []);

  const lastAssistantMessage = useMemo(
    () => (messages.length > 0 ? messages.filter((m) => m.role === 'assistant').pop() ?? null : null),
    [messages]
  );

  const currentQuestion = useMemo((): Question | null => {
    const lastAssistantQ = lastAssistantMessage
      ? normalizeQuestion(lastAssistantMessage.question ?? null)
      : null;
    if (lastAssistantMessage && lastAssistantMessage.id === completedMessageId) {
      return lastAssistantQ;
    }
    if (
      isTyping &&
      workZonesInteractiveSettledRef.current &&
      lastAssistantQ?.type === 'interactive' &&
      lastAssistantQ?.interactiveType === 'workZones'
    ) {
      return lastAssistantQ;
    }
    return null;
  }, [lastAssistantMessage, completedMessageId, isTyping, normalizeQuestion]);

  const currentField = currentQuestion?.field ?? null;
  const currentFieldValue = currentField
    ? state.accountType === 'PERSON'
      ? (state.personaForm as any)[currentField]
      : (state.empresaForm as any)[currentField]
    : null;
  const isOptionalField = ['tel', 'webSite', 'bio'].includes(currentField ?? '');
  const canKeepRequiredField =
    ['name', 'lastName', 'companyName'].includes(currentField ?? '') && !!currentFieldValue;
  const shouldShowSkip =
    reviewModeRef.current ||
    (currentQuestion?.type === 'text' && (isOptionalField || canKeepRequiredField));
  const skipLabel = reviewModeRef.current || canKeepRequiredField ? 'Mantener' : 'Omitir';

  const addMessage = useCallback((message: Omit<Message, 'id' | 'timestamp'>) => {
    const newMessage: Message = {
      ...message,
      id: `msg-${Date.now()}-${Math.random()}`,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, newMessage]);
    return newMessage;
  }, []);

  const addAssistantMessage = useCallback(
    (content: string, question?: Question | null, delay: number = 0, opts?: { instant?: boolean }) => {
      setTimeout(() => {
        const normalizedQuestion = normalizeQuestion(question ?? null);
        pendingQuestionRef.current = normalizedQuestion;
        const createdMessage = addMessage({ role: 'assistant', content, question: normalizedQuestion });
        if (opts?.instant) {
          setIsTyping(false);
          setCompletedMessageId(createdMessage.id);
          if (normalizedQuestion?.type === 'interactive' && normalizedQuestion?.interactiveType === 'workZones') {
            workZonesInteractiveSettledRef.current = true;
          }
          pendingQuestionRef.current = null;
          activeTypingMessageIdRef.current = null;
          lastTypingCompleteRef.current = createdMessage.id;
          return;
        }
        setIsTyping(true);
        setCompletedMessageId(null);
        activeTypingMessageIdRef.current = createdMessage.id;
      }, delay);
    },
    [addMessage, normalizeQuestion]
  );

  const beginAssistantTransition = useCallback(() => {
    setCompletedMessageId(null);
  }, []);

  const getBasicDataFields = useCallback((accountType: OnboardingV2State['accountType']) => {
    if (accountType === 'PERSON') {
      return ['name', 'lastName', 'tel', 'webSite', 'bio'];
    }
    if (accountType === 'COMPANY') {
      return ['companyName', 'tel', 'webSite', 'bio'];
    }
    return [];
  }, []);

  const getReviewMessage = useCallback((field: string, currentState: OnboardingV2State): AgentMessage => {
    const isPerson = currentState.accountType === 'PERSON';
    if (field === 'name') {
      const currentValue = currentState.personaForm.name;
      return {
        content: `¿Querés cambiar tu nombre? ${currentValue ? `Actual: ${currentValue}.` : ''}`,
        question: { id: 'name', type: 'text', field: 'name', placeholder: 'Escribí tu nombre...' },
      };
    }
    if (field === 'lastName') {
      const currentValue = currentState.personaForm.lastName;
      return {
        content: `¿Querés cambiar tu apellido? ${currentValue ? `Actual: ${currentValue}.` : ''}`,
        question: { id: 'lastName', type: 'text', field: 'lastName', placeholder: 'Escribí tu apellido...' },
      };
    }
    if (field === 'companyName') {
      const currentValue = currentState.empresaForm.companyName;
      return {
        content: `¿Querés cambiar el nombre de tu empresa? ${currentValue ? `Actual: ${currentValue}.` : ''}`,
        question: { id: 'companyName', type: 'text', field: 'companyName', placeholder: 'Escribí el nombre de tu empresa...' },
      };
    }
    if (field === 'tel') {
      const currentValue = isPerson ? currentState.personaForm.tel : currentState.empresaForm.tel;
      return {
        content: `¿Querés cambiar tu teléfono? ${currentValue ? `Actual: ${currentValue}.` : ''} Actualizalo o tocá "Mantener" para conservarlo.`,
        question: { id: 'tel', type: 'text', field: 'tel', placeholder: 'Escribí tu teléfono (solo números)...' },
      };
    }
    if (field === 'webSite') {
      const currentValue = isPerson ? currentState.personaForm.webSite : currentState.empresaForm.webSite;
      return {
        content: `¿Querés cambiar tu sitio web? ${currentValue ? `Actual: ${currentValue}.` : ''} Actualizalo o tocá "Mantener" para conservarlo.`,
        question: { id: 'webSite', type: 'text', field: 'webSite', placeholder: 'Escribí tu sitio web (opcional)...' },
      };
    }
    const currentValue = isPerson ? currentState.personaForm.bio : currentState.empresaForm.bio;
    return {
      content: `¿Querés cambiar tu descripción? ${currentValue ? `Actual: ${currentValue}.` : ''} Actualizala o tocá "Mantener" para conservarla.`,
      question: { id: 'bio', type: 'text', field: 'bio', placeholder: isPerson ? 'Contame sobre vos...' : 'Contame sobre tu empresa...' },
    };
  }, []);

  const handleTextResponse = useCallback((value: string, field: string) => {
    beginAssistantTransition();
    const form = state.accountType === 'PERSON' ? state.personaForm : state.empresaForm;
    const updateForm = state.accountType === 'PERSON' ? setPersonaForm : setEmpresaForm;

    updateForm({
      ...form,
      [field]: value,
    });

    addMessage({ role: 'user', content: value });

    setTimeout(async () => {
      const updatedForm = state.accountType === 'PERSON' 
        ? { ...state.personaForm, [field]: value }
        : { ...state.empresaForm, [field]: value };
      
      const updatedState = {
        ...state,
        [state.accountType === 'PERSON' ? 'personaForm' : 'empresaForm']: updatedForm,
      };

      if (reviewModeRef.current) {
        const nextIndex = reviewIndexRef.current + 1;
        if (nextIndex < reviewFieldsRef.current.length) {
          reviewIndexRef.current = nextIndex;
          const nextField = reviewFieldsRef.current[nextIndex];
          const reviewMessage = getReviewMessage(nextField, updatedState);
          addAssistantMessage(reviewMessage.content, reviewMessage.question, 300);
          return;
        }
        reviewModeRef.current = false;
        reviewFieldsRef.current = [];
        reviewIndexRef.current = 0;
        setIsReviewMode(false);
      }

      const context = {
        state: updatedState,
        messages: [...messages, { role: 'user' as const, content: value }],
        lastUserMessage: value,
        lastAction: `text_${field}`,
      };

      try {
        const agentResponse = await onboardingAgent.getResponse(context);
        addAssistantMessage(agentResponse.content, agentResponse.question, 300);
      } catch {
      }
    }, 200);
  }, [beginAssistantTransition, state, setPersonaForm, setEmpresaForm, addMessage, addAssistantMessage, messages, getReviewMessage]);

  const handleInteractiveAction = useCallback(async (action: string, data?: any) => {
    if (action === 'accountType') {
      const wasAccountTypeSet = !!state.accountType;
      beginAssistantTransition();
      setAccountType(data);
      addMessage({ role: 'user', content: data === 'PERSON' ? 'Profesional' : 'Empresa' });
      setTimeout(async () => {
        if (wasAccountTypeSet) {
          reviewFieldsRef.current = getBasicDataFields(data);
          reviewIndexRef.current = 0;
          reviewModeRef.current = reviewFieldsRef.current.length > 0;
          setIsReviewMode(reviewModeRef.current);
          if (reviewModeRef.current) {
            const reviewState = { ...state, accountType: data };
            const reviewMessage = getReviewMessage(reviewFieldsRef.current[0], reviewState);
            addAssistantMessage(reviewMessage.content, reviewMessage.question, 300);
            return;
          }
        }
        const updatedState = {
          ...state,
          accountType: data,
        };
        const context = {
          state: updatedState,
          messages: [...messages, { role: 'user' as const, content: data === 'PERSON' ? 'Profesional' : 'Empresa' }],
          lastUserMessage: data === 'PERSON' ? 'Profesional' : 'Empresa',
          lastAction: action,
        };
        try {
          const agentResponse = await onboardingAgent.getResponse(context);
          addAssistantMessage(agentResponse.content, agentResponse.question, 500);
        } catch {
        }
      }, 100);
    } else if (action === 'skill') {
      if (data.action === 'add') {
        addSkill(data.skill);
        pendingSkillResetRef.current = true;
      } else if (data.action === 'remove') {
        removeSkill(data.skillId);
        addMessage({ role: 'user', content: 'Removí la habilidad' });
      }
    } else if (action === 'profileCategory') {
      const current = state.preferredProfileCategories || [];
      const categoryId = data?.categoryId;
      if (typeof categoryId === 'number') {
        const isSelected = current.includes(categoryId);
        const updated = isSelected
          ? current.filter((id) => id !== categoryId)
          : [...current, categoryId];
        setPreferredProfileCategories(updated);
      }
    } else if (action === 'removeProfileCategory') {
      if (typeof data?.categoryId === 'number') {
        removePreferredProfileCategory(data.categoryId);
      }
    } else if (action === 'skillsComplete') {
      if (state.userSkills.length === 0) {
        addAssistantMessage('Agregá al menos una habilidad para continuar.', getQuestionForMissingStep('skills'), 300);
        setShowReturnToFinish(true);
        return;
      }
      setShowReturnToFinish(false);
      beginAssistantTransition();
      setTimeout(async () => {
        const context = {
          state,
          messages: [...messages, { role: 'user' as const, content: 'Continuar' }],
          lastUserMessage: 'Continuar',
          lastAction: action,
        };
        try {
          const agentResponse = await onboardingAgent.getResponse(context);
          addAssistantMessage(agentResponse.content, agentResponse.question, 500);
        } catch {
        }
      }, 100);
    } else if (action === 'profilesComplete') {
      if ((state.preferredProfileCategories?.length ?? 0) === 0) {
        addAssistantMessage('Seleccioná al menos una categoría de perfiles con quien conectar.', getQuestionForMissingStep('profiles'), 300);
        setShowReturnToFinish(true);
        return;
      }
      setShowReturnToFinish(false);
      beginAssistantTransition();
      setTimeout(async () => {
        const context = {
          state,
          messages: [...messages, { role: 'user' as const, content: 'Continuar' }],
          lastUserMessage: 'Continuar',
          lastAction: action,
        };
        try {
          const agentResponse = await onboardingAgent.getResponse(context);
          addAssistantMessage(agentResponse.content, agentResponse.question, 500);
        } catch {
        }
      }, 100);
    } else if (action === 'selectJurisdiction') {
      getCities(data.jurisdictionId);
    } else if (action === 'workZone') {
      if (data.action === 'allCountry') {
        const snap = onboardingJobBoardSnapshotAllCountry();
        setWorkCoverageKind(snap.workCoverageKind);
        setAllCountry(snap.allCountry);
        setWorkZones(snap.workZones);
        setInfluenceMap({
          lat: snap.influenceLat,
          lng: snap.influenceLng,
          radiusKm: snap.influenceRadiusKm,
          placeLabel: snap.influencePlaceLabel,
        });
        addMessage({ role: 'user', content: 'Todo el país' });
        setTimeout(async () => {
          const updatedState = mergeOnboardingJobBoardCoverage(state, snap);
          const context = {
            state: updatedState,
            messages: [...messages, { role: 'user' as const, content: 'Todo el país' }],
            lastUserMessage: 'Todo el país',
            lastAction: action,
          };
          try {
            const agentResponse = await onboardingAgent.getResponse(context);
            addAssistantMessage(agentResponse.content, agentResponse.question, 300, { instant: true });
          } catch {
          }
        }, 100);
      } else if (data.action === 'specificZones') {
        const partial = onboardingJobBoardSnapshotSpecificZones();
        setWorkCoverageKind(partial.workCoverageKind);
        setAllCountry(partial.allCountry);
      } else if (data.action === 'add') {
        const z = onboardingJobBoardSnapshotSpecificZones();
        setWorkCoverageKind(z.workCoverageKind);
        setAllCountry(z.allCountry);
        setWorkZones([...state.workZones, data.zone]);
        addMessage({ role: 'user', content: `Agregué: ${data.zone.city || data.zone.name}` });
      } else if (data.action === 'remove') {
        const updatedZones = state.workZones.filter((z: any, index: number) => {
          if (data.zoneId === undefined || data.zoneId === null) {
            return true;
          }
          if (typeof data.zoneId === 'number') {
            return z.id !== data.zoneId && index !== data.zoneId;
          }
          return z.id !== data.zoneId;
        });
        setWorkZones(updatedZones);
        addMessage({ role: 'user', content: 'Removí la zona' });
      }
    } else if (action === 'workZonesComplete') {
      if (!hasWorkZonesStepCompleted(state)) {
        addAssistantMessage(ONBOARDING_WORK_ZONES_AGENT.incompleteContinue, getQuestionForMissingStep('zones'), 300);
        setShowReturnToFinish(true);
        return;
      }
      setShowReturnToFinish(false);
      beginAssistantTransition();
      const context = {
        state,
        messages: [...messages, { role: 'user' as const, content: 'Continuar' }],
        lastUserMessage: 'Continuar',
        lastAction: action,
      };
      try {
        const agentResponse = await onboardingAgent.getResponse(context);
        addAssistantMessage(agentResponse.content, agentResponse.question, 500);
      } catch {
      }
    } else if (action === 'finishOnboarding') {
      const finishValidation = validateRequiredOnboardingData(state);
      if (!finishValidation.valid) {
        const firstMissing = finishValidation.missing[0];
        addAssistantMessage(getValidationErrorMessage(finishValidation.missing), getQuestionForMissingStep(firstMissing), 300);
        setShowReturnToFinish(true);
        return;
      }
      beginAssistantTransition();
      const context = {
        state,
        messages: [...messages, { role: 'user' as const, content: 'Finalizar onboarding' }],
        lastUserMessage: 'Finalizar onboarding',
        lastAction: action,
      };
      try {
        const agentResponse = await onboardingAgent.getResponse(context);
        addAssistantMessage(agentResponse.content, agentResponse.question, 300);
      } catch {
      }
    } else if (action === 'summaryEdit') {
      beginAssistantTransition();
      const context = {
        state,
        messages: [...messages, { role: 'user' as const, content: 'Editar pasos' }],
        lastUserMessage: 'Editar pasos',
        lastAction: action,
      };
      try {
        const agentResponse = await onboardingAgent.getResponse(context);
        addAssistantMessage(agentResponse.content, agentResponse.question, 300);
      } catch {
      }
    } else if (action === 'returnToCompleteActions') {
      setShowReturnToFinish(false);
      beginAssistantTransition();
      addAssistantMessage(
        'Podés revisar o cambiar pasos, o finalizar el onboarding para guardar tu perfil.',
        { id: 'completeActions', type: 'interactive', interactiveType: 'completeActions' },
        300
      );
    } else if (action === 'confirmSummary') {
      const summaryValidation = validateRequiredOnboardingData(state);
      if (!summaryValidation.valid) {
        const firstMissing = summaryValidation.missing[0];
        addAssistantMessage(getValidationErrorMessage(summaryValidation.missing), getQuestionForMissingStep(firstMissing), 300);
        setShowReturnToFinish(true);
        return;
      }
      setIsProcessing(true);
      addMessage({ role: 'user', content: 'Confirmar y finalizar' });
      try {
        await updateProfile();
        const latestUser = (store.getState() as { general?: { userData?: Record<string, unknown> } }).general
          ?.userData;
        if (userHasCommerceOnboardingCategory(state) && !userHasCommerceRegistration(latestUser)) {
          beginAssistantTransition();
          addAssistantMessage(
            'Tu perfil ya está guardado. Si tenés comercio o vendés materiales, podés registrarlo para conectar tu listado de precios con pedidos de la comunidad.',
            {
              id: 'commerceOffer',
              type: 'interactive',
              interactiveType: 'commerceOffer',
            },
            200
          );
        } else {
          navigate('/dashboard', { state: { showProfilePostModal: true } });
        }
      } catch {
      } finally {
        setIsProcessing(false);
      }
    } else if (action === 'commerceOnboardingSkip') {
      beginAssistantTransition();
      addMessage({ role: 'user', content: currentQuestion?.interactiveType === 'commerceForm' ? 'Omitir' : 'Ahora no' });
      navigate('/dashboard', { state: { showProfilePostModal: true } });
    } else if (action === 'commerceOnboardingContinue') {
      const latestForContinue = (store.getState() as { general?: { userData?: Record<string, unknown> } }).general
        ?.userData;
      if (userHasCommerceRegistration(latestForContinue)) {
        navigate('/dashboard', { state: { showProfilePostModal: true } });
        return;
      }
      beginAssistantTransition();
      addMessage({ role: 'user', content: 'Registrar mi comercio' });
      addAssistantMessage(
        'Completá los datos de tu comercio. Podés ajustar nombre o descripción si hace falta.',
        { id: 'commerceForm', type: 'interactive', interactiveType: 'commerceForm' },
        200
      );
    } else if (action === 'commerceOnboardingSubmit') {
      const latestUser = (store.getState() as { general?: { userData?: Record<string, unknown> } }).general
        ?.userData;
      if (userHasCommerceRegistration(latestUser)) {
        navigate('/dashboard', { state: { showProfilePostModal: true } });
        return;
      }
      const d = data as Record<string, unknown> | undefined;
      const userId = Number(d?.userId);
      const name = typeof d?.name === 'string' ? d.name : '';
      const description = typeof d?.description === 'string' ? d.description : '';
      const zone = typeof d?.zone === 'string' ? d.zone : '';
      const jurisdiccionId = d?.jurisdiccionId != null ? Number(d.jurisdiccionId) : NaN;
      if (!userId || !name.trim() || !description.trim() || !zone.trim() || !Number.isFinite(jurisdiccionId)) {
        return;
      }
      const deliveryScope: CommerceDeliveryScope =
        d?.deliveryScope === 'LOCAL' ||
        d?.deliveryScope === 'NATIONWIDE' ||
        d?.deliveryScope === 'LISTED_ZONES'
          ? d.deliveryScope
          : 'NATIONWIDE';
      const rawRadius = d?.deliveryRadiusKm;
      const deliveryRadiusKm =
        deliveryScope === 'LOCAL'
          ? Number.isFinite(Number(rawRadius))
            ? Math.min(
                COMMERCE_DELIVERY_RADIUS_UI_KM_MAX,
                Math.max(COMMERCE_DELIVERY_RADIUS_KM_MIN, Number(rawRadius))
              )
            : 50
          : undefined;
      const latRaw = d?.latitude;
      const lngRaw = d?.longitude;
      const latitude =
        deliveryScope === 'LOCAL' && latRaw != null && Number.isFinite(Number(latRaw)) ? Number(latRaw) : undefined;
      const longitude =
        deliveryScope === 'LOCAL' && lngRaw != null && Number.isFinite(Number(lngRaw)) ? Number(lngRaw) : undefined;
      let listedGeorefZonaIds: string[] | undefined;
      if (deliveryScope === 'LISTED_ZONES') {
        const rawListed = d?.listedGeorefZonaIds;
        const ids = Array.isArray(rawListed)
          ? rawListed
              .filter((x): x is string => typeof x === 'string' && x.length > 0)
              .slice(0, COMMERCE_LISTED_ZONES_ID_MAX)
          : [];
        if (ids.length < COMMERCE_LISTED_ZONES_ID_MIN) {
          dispatch(
            openMainModal({
              title: 'Zonas del listado',
              body: 'Elegí al menos una zona válida del catálogo de tu provincia antes de guardar.',
              iconType: 'error',
              isSuccess: true,
              confirmHandler: null,
            })
          );
          return;
        }
        listedGeorefZonaIds = ids;
      }
      setIsProcessing(true);
      dispatch(setLoginAnimationType('commerceSave'));
      dispatch(setLoginAnimation(true));
      try {
        await createCommerce({
          name,
          description,
          zone,
          delivery: !!d?.delivery,
          delivery_condition: typeof d?.delivery_condition === 'string' ? d.delivery_condition : undefined,
          url_site_web: typeof d?.url_site_web === 'string' ? d.url_site_web : undefined,
          jurisdiccionId,
          userId,
          logo: d?.logo instanceof File ? d.logo : null,
          deliveryScope,
          deliveryRadiusKm,
          latitude,
          longitude,
          listedGeorefZonaIds,
        });
        addMessage({ role: 'user', content: 'Listo, registré mi comercio' });
        navigate('/dashboard', { state: { showProfilePostModal: true } });
      } catch (err: any) {
        const msg = err?.response?.data?.message || 'No pudimos registrar el comercio. Intentá de nuevo.';
        dispatch(
          openMainModal({
            title: 'Registro de comercio',
            body: msg,
            iconType: 'error',
            isSuccess: true,
            confirmHandler: null,
          })
        );
      } finally {
        dispatch(setLoginAnimation(false));
        dispatch(setLoginAnimationType('login'));
        setIsProcessing(false);
      }
    }
  }, [beginAssistantTransition, state, setAccountType, addSkill, removeSkill, setWorkZones, setAllCountry, setWorkCoverageKind, setInfluenceMap, addMessage, addAssistantMessage, getCities, messages, updateProfile, setPreferredProfileCategories, removePreferredProfileCategory, navigate, getBasicDataFields, getReviewMessage, validateRequiredOnboardingData, getValidationErrorMessage, getQuestionForMissingStep, dispatch, currentQuestion, store]);

  const handleSendMessage = useCallback(() => {
    if (!inputValue.trim() || isProcessing || !currentQuestion) return;

    const value = inputValue.trim();
    setInputValue('');

    if (currentQuestion.type === 'text' && currentQuestion.field) {
      setIsProcessing(true);
      handleTextResponse(value, currentQuestion.field);
      setTimeout(() => {
        setIsProcessing(false);
      }, 500);
    }
  }, [inputValue, isProcessing, currentQuestion, handleTextResponse]);

  const handleSkipField = useCallback(async () => {
    if (!currentQuestion || currentQuestion.type !== 'text' || !currentQuestion.field) return;
    
    const optionalFields = ['tel', 'webSite', 'bio'];
    const keepableFields = ['name', 'lastName', 'companyName'];
    const isKeepable =
      keepableFields.includes(currentQuestion.field) &&
      !!currentFieldValue;
    if (!reviewModeRef.current && !optionalFields.includes(currentQuestion.field) && !isKeepable) return;

    setIsProcessing(true);
    beginAssistantTransition();
    setInputValue('');

    const form = state.accountType === 'PERSON' ? state.personaForm : state.empresaForm;
    const shouldKeep = isKeepable || reviewModeRef.current;
    const updatedForm = shouldKeep
      ? form
      : { ...form, [currentQuestion.field]: null };
    if (!shouldKeep) {
      const updateForm = state.accountType === 'PERSON' ? setPersonaForm : setEmpresaForm;
      updateForm(updatedForm);
    }
    const updatedState = {
      ...state,
      [state.accountType === 'PERSON' ? 'personaForm' : 'empresaForm']: updatedForm,
    };
    const userLabel = shouldKeep ? 'Mantener' : 'Omitir';
    addMessage({ role: 'user', content: userLabel });

    if (reviewModeRef.current) {
      const nextIndex = reviewIndexRef.current + 1;
      if (nextIndex < reviewFieldsRef.current.length) {
        reviewIndexRef.current = nextIndex;
        const nextField = reviewFieldsRef.current[nextIndex];
        const reviewMessage = getReviewMessage(nextField, updatedState);
        addAssistantMessage(reviewMessage.content, reviewMessage.question, 300);
        setIsProcessing(false);
        return;
      }
      reviewModeRef.current = false;
      reviewFieldsRef.current = [];
      reviewIndexRef.current = 0;
      setIsReviewMode(false);
    }

    const context = {
      state: updatedState,
      messages: [...messages, { role: 'user' as const, content: userLabel }],
      lastUserMessage: userLabel,
      lastAction: `${shouldKeep ? 'keep' : 'skip'}_${currentQuestion.field}`,
    };

    try {
      const agentResponse = await onboardingAgent.getResponse(context);
      addAssistantMessage(agentResponse.content, agentResponse.question, 300);
    } catch {
    } finally {
      setIsProcessing(false);
    }
  }, [beginAssistantTransition, currentQuestion, state, setPersonaForm, setEmpresaForm, addMessage, addAssistantMessage, messages, getReviewMessage, currentFieldValue]);

  useEffect(() => {
    if (messages.length === 0 && !initializedRef.current) {
      initializedRef.current = true;
      loadSkillsAndCategories();
      const hasProgressData = hasProgress();
      const currentState = state;
      setTimeout(async () => {
        try {
          let agentMessage: AgentMessage;
          if (currentState.isFromSkipOnboarding) {
            agentMessage = {
              content: 'Antes de finalizar, revisá el resumen para confirmar que todo esté correcto.',
              question: {
                id: 'summary',
                type: 'interactive',
                interactiveType: 'summary',
              },
            };
          } else if (hasProgressData) {
            agentMessage = await onboardingAgent.getResumeMessage(currentState);
          } else {
            agentMessage = await onboardingAgent.getWelcomeMessage();
          }
          
          addAssistantMessage(agentMessage.content, agentMessage.question, 0);
        } catch {
          setIsTyping(false);
        }
      }, 300);
    }
  }, [addAssistantMessage, loadSkillsAndCategories, hasProgress, messages.length, state]);

  const navigationPendingRef = useRef<boolean>(false);
  const navigationTargetRef = useRef<OnboardingStep | null>(null);

  const getNavigationMessage = useCallback((targetStep: OnboardingStep, currentState: OnboardingV2State): AgentMessage => {
    if (!currentState.accountType) {
      return {
        content: 'Primero necesito saber qué tipo de cuenta querés crear. Seleccioná una opción.',
        question: {
          id: 'accountType',
          type: 'interactive',
          interactiveType: 'accountType',
        },
      };
    }

    if (targetStep === 'accountType') {
      return {
        content: 'Podés cambiar el tipo de cuenta si lo necesitás. Seleccioná una opción.',
        question: {
          id: 'accountType',
          type: 'interactive',
          interactiveType: 'accountType',
        },
      };
    }

    if (targetStep === 'basicData') {
      const fields = reviewFieldsRef.current.length > 0
        ? reviewFieldsRef.current
        : getBasicDataFields(currentState.accountType);
      if (fields.length > 0) {
        reviewFieldsRef.current = fields;
        reviewIndexRef.current = 0;
        reviewModeRef.current = true;
        setIsReviewMode(true);
        return getReviewMessage(fields[0], currentState);
      }
    }

    if (targetStep === 'skills') {
      if (currentState.userSkills.length === 0) {
        return {
          content: 'Podés revisar o cambiar tus habilidades. Buscá y agregá las que correspondan.',
          question: {
            id: 'skills',
            type: 'interactive',
            interactiveType: 'skills',
          },
        };
      }
      if (currentState.preferredProfileCategories.length === 0) {
        return {
          content: 'Ahora elegí con qué perfiles querés conectar. Seleccioná las categorías que te interesan.',
          question: {
            id: 'profiles',
            type: 'interactive',
            interactiveType: 'profiles',
          },
        };
      }
      return {
        content: 'Podés revisar o cambiar tus habilidades y perfiles. Buscá y ajustá lo que necesites.',
        question: {
          id: 'skills',
          type: 'interactive',
          interactiveType: 'skills',
        },
      };
    }

    if (targetStep === 'workZones') {
      return {
        content: ONBOARDING_WORK_ZONES_AGENT.navigateEdit,
        question: {
          id: 'workZones',
          type: 'interactive',
          interactiveType: 'workZones',
        },
      };
    }

    if (targetStep === 'summary') {
      return {
        content: 'Revisá tu perfil antes de confirmar.',
        question: {
          id: 'summary',
          type: 'interactive',
          interactiveType: 'summary',
        },
      };
    }

    return {
      content: 'Podés revisar o cambiar pasos, o finalizar el onboarding para guardar tu perfil.',
      question: {
        id: 'completeActions',
        type: 'interactive',
        interactiveType: 'completeActions',
      },
    };
  }, [getBasicDataFields, getReviewMessage]);

  useEffect(() => {
    if (navigationPendingRef.current && messages.length === 0 && !isTyping && !isProcessing) {
      navigationPendingRef.current = false;
      const hasProgressData = hasProgress();
      setTimeout(async () => {
        try {
          let agentMessage: AgentMessage;
          if (navigationTargetRef.current) {
            agentMessage = getNavigationMessage(navigationTargetRef.current, state);
            navigationTargetRef.current = null;
          } else if (state.isFromSkipOnboarding) {
            agentMessage = {
              content: 'Antes de finalizar, revisá el resumen para confirmar que todo esté correcto.',
              question: {
                id: 'summary',
                type: 'interactive',
                interactiveType: 'summary',
              },
            };
          } else if (hasProgressData) {
            agentMessage = await onboardingAgent.getResumeMessage(state);
          } else {
            agentMessage = await onboardingAgent.getWelcomeMessage();
          }
          
          addAssistantMessage(agentMessage.content, agentMessage.question, 0);
        } catch {
          setIsTyping(false);
        }
      }, 300);
    }
  }, [messages.length, isTyping, isProcessing, hasProgress, state, addAssistantMessage, getNavigationMessage]);

  const navigateToStep = useCallback((targetStep: OnboardingStep) => {
    const lastMsg = messages.length > 0 ? messages.filter((m: Message) => m.role === 'assistant').pop() : null;
    if (lastMsg?.question?.interactiveType === 'completeActions') {
      setShowReturnToFinish(true);
    }
    setMessages([]);
    initializedRef.current = false;
    navigationPendingRef.current = true;
    navigationTargetRef.current = targetStep;
    if (targetStep === 'basicData') {
      reviewFieldsRef.current = getBasicDataFields(state.accountType);
      reviewIndexRef.current = 0;
      reviewModeRef.current = reviewFieldsRef.current.length > 0;
      setIsReviewMode(reviewModeRef.current);
    } else {
      reviewFieldsRef.current = [];
      reviewIndexRef.current = 0;
      reviewModeRef.current = false;
      setIsReviewMode(false);
    }
  }, [getBasicDataFields, state.accountType, messages]);

  const handleInteractiveExitComplete = useCallback(() => {
    if (pendingSkillResetRef.current) {
      setSelectedCategoryFilter(null);
      pendingSkillResetRef.current = false;
    }
  }, [setSelectedCategoryFilter]);

  const lastTypingCompleteRef = useRef<string | null>(null);
  const handleTypingComplete = useCallback((messageId?: string) => {
    if (messageId && lastTypingCompleteRef.current === messageId) {
      return;
    }
    if (!messageId || messageId !== activeTypingMessageIdRef.current) {
      return;
    }
    lastTypingCompleteRef.current = messageId;
    activeTypingMessageIdRef.current = null;
    if (typingTimeoutRef.current !== null) {
      window.clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    const it = pendingQuestionRef.current?.interactiveType;
    const shouldDelaySummary =
      it === 'summary' || it === 'commerceOffer' || it === 'commerceForm';
    const applyTypingComplete = () => {
      const pending = pendingQuestionRef.current;
      if (pending?.type === 'interactive' && pending.interactiveType === 'workZones') {
        workZonesInteractiveSettledRef.current = true;
      }
      setIsTyping(false);
      setCompletedMessageId(messageId);
      pendingQuestionRef.current = null;
    };
    if (shouldDelaySummary) {
      window.setTimeout(applyTypingComplete, 800);
      return;
    }
    applyTypingComplete();
  }, []);

  useEffect(() => {
    if (!isTyping) {
      if (typingTimeoutRef.current !== null) {
        window.clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      return;
    }
    const messageId = activeTypingMessageIdRef.current;
    if (!messageId || !lastAssistantMessage || lastAssistantMessage.id !== messageId) {
      return;
    }
    const duration = Math.max(1, lastAssistantMessage.content.length) * 30;
    if (typingTimeoutRef.current !== null) {
      window.clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = window.setTimeout(() => {
      handleTypingComplete(messageId);
    }, duration + 200);
  }, [isTyping, lastAssistantMessage, handleTypingComplete]);

  const handleReturnToCompleteActions = useCallback(() => {
    handleInteractiveAction('returnToCompleteActions');
  }, [handleInteractiveAction]);

  return {
    messages,
    currentQuestion,
    inputValue,
    setInputValue,
    handleSendMessage,
    handleInteractiveAction,
    handleSkipField,
    isProcessing,
    isTyping,
    navigateToStep,
    handleInteractiveExitComplete,
    isReviewMode,
    shouldShowSkip,
    skipLabel,
    handleTypingComplete,
    showReturnToFinish,
    handleReturnToCompleteActions,
  };
};
